import { useEffect, useRef, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { useReactTable, getCoreRowModel, getSortedRowModel, SortingState, ColumnDef, Cell, Row, OnChangeFn } from '@tanstack/react-table';
import { VirtuosoTableRow } from './VirtuosoTableRow';
import { VirtuosoTableHeader } from './VirtuosoTableHeader';
import { EmptyPlaceholder } from './EmptyPlaceholder';

interface VirtualizedTableProps<T extends object> {
    data: T[];
    columns: ColumnDef<T, any>[];
    emptyPlaceholder: string | undefined;
    headerRowStyle?: React.CSSProperties;
    headerCellStyle?: React.CSSProperties | ((header: any) => React.CSSProperties);
    rowStyle?: React.CSSProperties | ((row: Row<T>) => React.CSSProperties);
    cellStyle?: React.CSSProperties | ((cell: Cell<T, unknown>) => React.CSSProperties);
    tableConfig: any;
    emptyPlaceholderTitle?: string;
    selItem?: number | string;
    primaryKey: string;
    onRowClick?: (row: any) => void;
    sorting?: SortingState;
    onSortingChange?: OnChangeFn<SortingState>;
}

export const VirtualizedTable = <T extends object>({ data, columns, emptyPlaceholder, headerRowStyle, headerCellStyle, rowStyle, cellStyle, tableConfig, emptyPlaceholderTitle, onRowClick, selItem, primaryKey, sorting: externalSorting, onSortingChange }: VirtualizedTableProps<T>) => {
    const virtuosoRef = useRef<any>(null);
    const [internalSorting, setInternalSorting] = useState<SortingState>([]);

    const sorting = externalSorting ?? internalSorting;
    const setSorting = onSortingChange ?? setInternalSorting;

    const table = useReactTable({
        data: Array.isArray(data) ? data : [],
        columns: Array.isArray(columns) ? columns : [],
        defaultColumn: { size: 175, },
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableSortingRemoval: false,
    });

    const { rows } = table.getRowModel();

    const selIndex = rows.findIndex((r: any) => r.original?.[primaryKey] === selItem);

    useEffect(() => {
        if (!isNaN(selIndex) && selIndex >= 0 && virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({
                index: selIndex,
                align: "center", // can be "start" | "center" | "end" | "auto"
                behavior: "auto",
            });
        }
    }, [selIndex]);

    const getColumnWidth = (column: any) => {
        // Check if column has custom size defined
        if (column.columnDef?.size) {
            return column.columnDef.size;
        }
        const isPrimary = tableConfig?.PRIMARY_KEY && column.id === tableConfig?.PRIMARY_KEY;
        const baseWidth = 175; // defaultColumn.size
        return isPrimary ? baseWidth * 1.5 : baseWidth;
    };

    return (
        <TableVirtuoso
            ref={virtuosoRef}
            totalCount={rows.length}
            components={{
                Table: ({ style, ...props }: any) => (
                    <table {...props} className="compare-table table w-100 m-0 h-100" style={{ tableLayout: 'auto' }}>
                        <colgroup>
                            {table.getAllColumns().map((column: any) => (
                                <col key={column.id} style={{ minWidth: getColumnWidth(column) }} />
                            ))}
                        </colgroup>
                        {props.children}
                    </table>
                ),
                TableRow: (props: any) => (
                    <VirtuosoTableRow
                        key={(rows[props['data-index']].original as Record<string, any>)[primaryKey]}
                        rows={rows}
                        rowStyle={rowStyle}
                        cellStyle={cellStyle}
                        tableConfig={tableConfig}
                        selIndex={selItem !== undefined ? selIndex : undefined}
                        onRowClick={onRowClick}
                        {...props}
                    />),
                EmptyPlaceholder: () => (
                    <EmptyPlaceholder
                        colSpan={columns.length}
                        message={emptyPlaceholder}
                        emptyPlaceholderTitle={emptyPlaceholderTitle}
                    />
                ),
            }}
            fixedHeaderContent={() => (
                <VirtuosoTableHeader
                    table={table}
                    headerRowStyle={headerRowStyle}
                    headerCellStyle={headerCellStyle}
                    tableConfig={tableConfig}
                />)}
        />
    );
};
